import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Users, ChartBar, FileText, Globe, DeviceMobile, SquaresFour,
  Megaphone, MagnifyingGlass, CurrencyDollar, Bank, UsersThree,
  Rocket, EnvelopeOpen, Article, ShoppingBag, Code, Palette, MapTrifold
} from 'phosphor-react';
import { AnimatedTitle } from '@/components/common/AnimatedTitle';

interface UseCaseItem {
  id: string;
  icon: React.ElementType;
  color: string;
}

const rows: UseCaseItem[][] = [
  // Row 1
  [
    { id: 'crm', icon: Users, color: 'text-blue-400 border-blue-500/30 hover:bg-blue-500/20' },
    { id: 'report', icon: ChartBar, color: 'text-green-400 border-green-500/30 hover:bg-green-500/20' },
    { id: 'contracts', icon: FileText, color: 'text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/20' },
    { id: 'website', icon: Globe, color: 'text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/20' },
    { id: 'mobile', icon: DeviceMobile, color: 'text-purple-400 border-purple-500/30 hover:bg-purple-500/20' },
    { id: 'dashboards', icon: SquaresFour, color: 'text-orange-400 border-orange-500/30 hover:bg-orange-500/20' },
  ],
  // Row 2
  [
    { id: 'marketing', icon: Megaphone, color: 'text-pink-400 border-pink-500/30 hover:bg-pink-500/20' },
    { id: 'seo', icon: MagnifyingGlass, color: 'text-indigo-400 border-indigo-500/30 hover:bg-indigo-500/20' },
    { id: 'sales', icon: CurrencyDollar, color: 'text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20' },
    { id: 'finance', icon: Bank, color: 'text-teal-400 border-teal-500/30 hover:bg-teal-500/20' },
    { id: 'hr', icon: UsersThree, color: 'text-rose-400 border-rose-500/30 hover:bg-rose-500/20' },
    { id: 'onboarding', icon: Rocket, color: 'text-violet-400 border-violet-500/30 hover:bg-violet-500/20' },
  ],
  // Row 3
  [
    { id: 'newsletter', icon: EnvelopeOpen, color: 'text-amber-400 border-amber-500/30 hover:bg-amber-500/20' },
    { id: 'blog', icon: Article, color: 'text-lime-400 border-lime-500/30 hover:bg-lime-500/20' },
    { id: 'ecommerce', icon: ShoppingBag, color: 'text-fuchsia-400 border-fuchsia-500/30 hover:bg-fuchsia-500/20' },
    { id: 'api', icon: Code, color: 'text-sky-400 border-sky-500/30 hover:bg-sky-500/20' },
    { id: 'design_system', icon: Palette, color: 'text-red-400 border-red-500/30 hover:bg-red-500/20' },
    { id: 'roadmap', icon: MapTrifold, color: 'text-slate-400 border-slate-500/30 hover:bg-slate-500/20' },
  ],
];

export function PerfectForSection() {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const x1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const x2 = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const x3 = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const transforms = [x1, x2, x3];

  return (
    <section ref={sectionRef} data-scroll-section className="relative py-32 px-6 overflow-hidden theme-bg">
      <div className="max-w-[1920px] mx-auto text-center">
        <AnimatedTitle
          text={t('perfectFor.title')}
          className="text-3xl md:text-5xl lg:text-6xl font-light mb-20 tracking-tight text-[color:var(--foreground)]"
        />

        <div className="flex flex-col gap-8 items-center">
          {rows.map((row, rowIndex) => (
            <motion.div
              key={rowIndex}
              style={{ x: transforms[rowIndex] }}
              className={`flex flex-wrap justify-center gap-4 w-full`}
            >
              {/* Duplicate items to ensure scrolling feels continuous if needed, but for now simple transform is fine */}
              <div className="flex gap-4">
                {row.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: rowIndex * 0.1 }}
                      className={`flex items-center gap-3 px-6 py-3.5 rounded border theme-border bg-[color:var(--card-bg)] transition-all duration-300 hover:scale-105 cursor-default group ${item.color} shadow-xl`}
                    >
                      <Icon className="w-5 h-5 transition-transform group-hover:scale-110" weight="fill" />
                      <span className="font-semibold whitespace-nowrap text-[color:var(--foreground)] transition-colors tracking-wide">
                        {t(`perfectFor.items.${item.id}`)}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
